import React, {
    ComponentType,
    FocusEvent,
    HTMLProps,
    ReactElement,
    useCallback,
    useRef,
    useState,
} from 'react';
import styled, { css, CSSProperties } from 'styled-components';
import { getTheme } from '../../utils/getTheme';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { Box, BoxProps } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { useCloseKeyboardOnScroll } from './hooks/useCloseKeyboardOnScroll';

type TextControlProps = BoxProps & {
    disabled?: boolean;
    focused?: boolean;
    error?: boolean;
    variant?: 'filled' | 'standard' | 'outline';
    flexwrap?: CSSProperties['flexWrap'];
    padding?: CSSProperties['paddingBottom'];
    alignItems?: CSSProperties['alignItems'];
    onClick?: React.MouseEventHandler<Element>;
};

const TextControl = styled(Box).withConfig({
    displayName: 'FormControl',
    shouldForwardProp: shouldIgnoreProp('disabled', 'focused', 'error', 'variant'),
})<TextControlProps>`
    display: flex;
    position: relative;
    align-items: baseline;
    transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    color: var(--color-primary);
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;

    ${(props) => css`
        width: ${props.width ? `${props.width}px` : '100%'};
    `};

    ${(props) => css`
        flex-wrap: ${props.flexwrap};
    `};

    ${(props) =>
        props.variant === 'filled' &&
        css`
            padding: 8px 16px;
            border-radius: 12px;
            color: var(--color-primary);
            background-color: var(--background-secondary);
        `}

    ${(props) =>
        props.variant === 'standard' &&
        css`
            border-bottom: 2px solid var(--divider);

            &::after {
                content: '';
                position: absolute;
                left: 0;
                right: 0;
                bottom: -2px;
                transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
                pointer-events: none;
                border-bottom: 2px solid var(--color-primary);
                transform: scaleX(0);
            }
        `}

    ${(props) =>
        props.variant === 'outline' &&
        css`
            padding: 8px 16px;
            border-radius: 12px;
            color: var(--color-primary);
            border: 1px solid var(--placeholder);
        `}

    ${(props) =>
        props.error &&
        css`
            border-color: var(--color-danger);
        `};

    ${(props) =>
        props.focused &&
        !props.error &&
        !props.disabled &&
        css`
            &::after {
                transform: scale(1);
            }
        `};

    ${(props) =>
        props.disabled &&
        css`
            cursor: not-allowed;
        `};

    ${(props) => css`
        padding: ${props.padding};
    `};

    ${(props) => css`
        align-items: ${props.alignItems};
    `};
`;

export interface ControlProps<TElement> extends HTMLProps<TElement> {
    variant?: 'standard' | 'filled' | 'outline';
    error?: boolean;
    prefix?: string;
    rows?: number;
    startAdornment?: ReactElement;
    endAdornment?: ReactElement;
    dataTestId?: DataTestId;
    // хак, из-за того, что проп disabled не прокидывается
    controlDisabled?: boolean;
    flexwrap?: CSSProperties['flexWrap'];
    padding?: CSSProperties['paddingBottom'];
    minHeight?: CSSProperties['minHeight'];
    controlWidth?: CSSProperties['width'];
    width?: CSSProperties['width'];
    alignItems?: CSSProperties['alignItems'];
    mb?: number;
    textOverflow?: CSSProperties['textOverflow'];
    cursor?: CSSProperties['cursor'];
}

export function makeTextControl<TElement extends HTMLElement>(
    component: ComponentType<HTMLProps<TElement>> | string,
) {
    const isDarkTheme = getTheme() === 'dark';

    const Control = styled(component)
        .attrs<ControlProps<TElement>, ControlProps<TElement>>(({ dataTestId, ...props }) => ({
            ...props,
            'data-test-id': dataTestId,
        }))
        .withConfig({ displayName: 'Control', shouldForwardProp: shouldIgnoreProp('dataTestId') })`
        resize: none;
        font: inherit;
        letter-spacing: inherit;
        color: var(--color-primary);
        padding: 4px 0 5px;
        border: 0;
        box-sizing: content-box;
        background: none;
        margin: 0;
        -webkit-tap-highlight-color: transparent;
        display: block;
        min-width: 0;
        width: 100%;
        outline: none;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        max-height: 50vh;

        ${(props) => css`
            width: ${props.width ? `${props.width}px` : '100%'};
        `};

        ${(props) => css`
            margin-bottom: ${(props.mb ?? 0) * 8}px;
        `};

        ${(props) =>
            props.rows &&
            css`
                min-height: calc(20px * ${props.rows});
            `};

        ${(props) =>
            props.textOverflow &&
            css`
                text-overflow: ${props.textOverflow};
            `};

        ${(props) =>
            props.cursor &&
            css`
                cursor: ${props.cursor};
            `};

        text-overflow: ellipsis;

        &[disabled] {
            opacity: 0.5;
            cursor: not-allowed;
        }

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        &[type='number'] {
            -moz-appearance: textfield;
        }

        &[type='date'] {
            color-scheme: ${isDarkTheme ? 'dark' : 'light'};
        }
    `;

    return (props: ControlProps<TElement>) => {
        const {
            error,
            variant = 'standard',
            startAdornment,
            endAdornment,
            flexwrap,
            padding,
            minHeight,
            controlWidth,
            alignItems,
            onClick,
            cursor,
            ...other
        } = props;
        const [focused, setFocused] = useState(false);
        const controlRef = useRef<TElement>(null);

        const onFocus = useCallback(
            (event: FocusEvent<TElement>) => {
                props.onFocus?.(event);
                setFocused(true);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [props.onFocus],
        );

        const onBlur = useCallback(
            (event: FocusEvent<TElement>) => {
                props.onBlur?.(event);
                setFocused(false);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [props.onBlur],
        );

        useCloseKeyboardOnScroll(controlRef);

        return (
            <TextControl
                error={error}
                onClick={onClick}
                focused={focused}
                variant={variant}
                disabled={props.disabled || props.controlDisabled}
                flexwrap={flexwrap}
                padding={padding}
                minHeight={minHeight}
                width={controlWidth}
                alignItems={alignItems}
            >
                {startAdornment}
                <Control
                    {...other}
                    ref={controlRef}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={props.disabled || props.controlDisabled}
                    cursor={cursor}
                />
                {endAdornment}
            </TextControl>
        );
    };
}
